import React, { useEffect, useRef, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Search, Plus, User, X } from "lucide-react";

// A searchable text-input that resolves to a client_id. If the typed value
// doesn't match any existing client, offers to quick-create one. Works with any
// upstream client list; it fetches once from /clients on mount unless a `clients`
// prop is provided.
export default function ClientAutocomplete({
    value,             // client_id or ""
    onChange,          // (client_id) => void
    onClientCreated,   // (client) => void  optional
    clients: initialClients,
    placeholder = "Cerca o scrivi un nuovo cliente…",
    collaboratorId = null,
    testId = "client-autocomplete",
    allowClear = true,
    allowCreate = true,
}) {
    const [clients, setClients] = useState(initialClients || []);
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!initialClients) {
            api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        }
    }, [initialClients]);

    // If value changes upstream, sync the input display
    useEffect(() => {
        if (value) {
            const c = clients.find((x) => x.id === value);
            if (c) setQuery(`${c.first_name || ""} ${c.last_name || ""}`.trim());
        } else {
            setQuery("");
        }
    }, [value, clients]);

    // Close on outside click
    useEffect(() => {
        const h = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const trimmed = query.trim().toLowerCase();
    const filtered = clients.filter((c) => {
        if (!trimmed) return true;
        const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
        return name.includes(trimmed) || (c.company || "").toLowerCase().includes(trimmed) ||
            (c.email || "").toLowerCase().includes(trimmed);
    }).slice(0, 20);

    const exact = clients.find((c) =>
        `${c.first_name || ""} ${c.last_name || ""}`.trim().toLowerCase() === trimmed
    );

    const pick = (c) => {
        setQuery(`${c.first_name || ""} ${c.last_name || ""}`.trim());
        setOpen(false);
        onChange && onChange(c.id);
    };

    const quickCreate = async () => {
        const parts = query.trim().split(/\s+/);
        const first_name = parts[0] || "Nuovo";
        const last_name = parts.slice(1).join(" ");
        setBusy(true); setErr("");
        try {
            const { data } = await api.post("/clients/quick",
                { first_name, last_name, collaborator_id: collaboratorId });
            setClients((prev) => [data, ...prev]);
            onClientCreated && onClientCreated(data);
            pick(data);
        } catch (e) { setErr(extractError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div ref={rootRef} className="relative" data-testid={testId}>
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); if (value) onChange && onChange(""); }}
                onFocus={() => setOpen(true)}
                placeholder={placeholder}
                data-testid={`${testId}-input`}
                className="w-full pl-7 pr-7 py-1.5 border border-neutral-300 rounded text-sm"
            />
            {value && allowClear && (
                <button type="button" onClick={() => { setQuery(""); onChange && onChange(""); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                    data-testid={`${testId}-clear`}>
                    <X size={12} />
                </button>
            )}
            {open && (
                <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-neutral-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
                    data-testid={`${testId}-dropdown`}>
                    {filtered.length === 0 && !allowCreate && (
                        <div className="p-3 text-xs text-neutral-500">Nessun cliente</div>
                    )}
                    {filtered.map((c) => (
                        <button key={c.id} type="button" onClick={() => pick(c)}
                            data-testid={`${testId}-option-${c.id}`}
                            className="w-full text-left px-3 py-2 hover:bg-neutral-50 border-b border-neutral-50 last:border-b-0">
                            <div className="text-sm text-neutral-900 flex items-center gap-1.5">
                                <User size={12} className="text-neutral-400" />
                                {`${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
                            </div>
                            {(c.company || c.email) && (
                                <div className="text-xs text-neutral-500 truncate ml-4">
                                    {c.company || c.email}
                                </div>
                            )}
                        </button>
                    ))}
                    {allowCreate && trimmed && !exact && (
                        <button type="button" onClick={quickCreate} disabled={busy}
                            data-testid={`${testId}-create`}
                            className="w-full text-left px-3 py-2 bg-neutral-50 hover:bg-neutral-100 border-t border-neutral-100 text-sm flex items-center gap-1.5">
                            <Plus size={12} className="text-neutral-500" />
                            {busy ? "Creazione…" : <>Crea nuovo cliente: <b className="ml-1">{query.trim()}</b></>}
                        </button>
                    )}
                </div>
            )}
            {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
        </div>
    );
}
